import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Bank, Factory, Cube, Question } from '@phosphor-icons/react';
import { ProductDetails, EntityDetails } from '../hooks/useEntityDetails';
import { SelectProduct } from './SelectProduct';
import { formatUnits } from 'viem';
import { useContractRead } from 'wagmi';
import { CONFIG } from '../config';
import { Abi } from 'viem';
import { useGetProperty, useGetImage } from '../utils/properties';
import { detectPngWithAlpha } from '../utils/imageDetection';
import { processImageData } from '../utils/ipfsUtils';

// Map product types to icons
const PRODUCT_TYPE_ICONS: Record<string, typeof Bank> = {
  'TrinityBond': Bank,
  'TrinityFactory': Factory,
  'TrinityProduct': Cube,
  'Unknown': Question,
};

interface ProductImageProps {
  productAddress: `0x${string}`;
}

function ProductImage({ productAddress }: { productAddress: `0x${string}` }) {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageData, setImageData] = useState<string>('');

  // Get product properties contract address
  const { data: propertiesAddress } = useContractRead({
    address: productAddress,
    abi: CONFIG.TRINITY_PRODUCT_ABI.abi as Abi,
    functionName: 'properties',
    enabled: !!productAddress,
  }) as { data: `0x${string}` };

  // Get product image from properties using getUIImage
  const { data: productImageUrl } = useContractRead({
    address: propertiesAddress as `0x${string}`,
    abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
    functionName: 'getUIImage',
    args: [0], // Get the first image (index 0)
    enabled: !!propertiesAddress && !!productAddress,
  }) as { data: string };

  // Process the image data using the utility function
  useEffect(() => {
    if (productImageUrl) {
      const processedUrl = processImageData(productImageUrl);
      setImageUrl(processedUrl);
      setImageData(productImageUrl);
    }
  }, [productImageUrl]);

  return (
    <div className="w-full h-[120px] bg-gray-800/50 rounded-lg overflow-hidden">
      <img 
        src={imageUrl} 
        alt="Product" 
        className={`w-full h-full ${detectPngWithAlpha(imageData || '') ? 'object-contain' : 'object-cover'}`}
        onError={(e) => {
          console.error('Error loading image:', e)
          setImageUrl('')
        }}
      />
    </div>
  )
}

interface ProductManagerProps {
  entity: EntityDetails;
  onProductSelect: (productAddress: `0x${string}` | undefined) => void;
  selectedProduct?: `0x${string}`;
}

export function ProductManager({ entity, onProductSelect, selectedProduct }: ProductManagerProps) {
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);

  // Log component state
  useEffect(() => {
    console.log('ProductManager state:', {
      entity: {
        address: entity?.address,
        name: entity?.name,
        products: entity?.products.map(p => ({
          address: p.address,
          name: p.name,
          className: p.className
        }))
      },
      selectedProduct,
      isCreatingProduct
    });
  }, [entity, selectedProduct, isCreatingProduct]);

  const handleProductSelect = (productAddress: `0x${string}`) => {
    console.log('ProductManager handleProductSelect:', {
      productAddress,
      currentlySelected: selectedProduct,
      entityProducts: entity?.products.map(p => p.address)
    });
    onProductSelect(productAddress);
  };

  // If entity is not loaded yet, show loading state
  if (!entity) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Products</h2>
        <button
          onClick={() => setIsCreatingProduct(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>

      {/* Create Product Modal */}
      <AnimatePresence>
        {isCreatingProduct && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-50 flex justify-center p-20 backdrop-blur-sm bg-black/50"
          >
            <div className="relative w-full max-w-4x top-[20vh] ">
              <button
                onClick={() => setIsCreatingProduct(false)}
                className="absolute top-4 right-4 text-white/60 hover:text-white"
              >
                <X size={24} />
              </button>
              <SelectProduct
                entity={entity}
                onCancel={() => setIsCreatingProduct(false)}
                onSuccess={(productAddress) => {
                  setIsCreatingProduct(false);
                  handleProductSelect(productAddress);
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Products Grid */}
      <div className="relative">
        <AnimatePresence mode="popLayout">
          {!entity?.products ? (
            <div className="text-center text-white/60 py-12">
              Loading products...
            </div>
          ) : entity.products.length === 0 ? (
            <div className="text-center text-white/60 py-12">
              No products yet. Click "Create Product" to get started.
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 2xl:grid-cols-10 gap-3">
              {entity.products.map((product) => {
                const ProductIcon = PRODUCT_TYPE_ICONS[product.className] || PRODUCT_TYPE_ICONS.Unknown;
                return (
                  <motion.div
                    key={product.address}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`glass-panel aspect-square p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedProduct === product.address ? 'bg-blue-500/30' : 'hover:bg-white/5'
                    }`}
                    onClick={() => handleProductSelect(product.address)}
                  >
                    <div className="h-full flex flex-col">
                      <ProductImage productAddress={product.address} />
                      <div className="mt-2">
                        <div className="flex items-start justify-end mb-1">
                          <div className="text-[10px] font-medium text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded">
                            Issues: {product.productCount || 0}
                          </div>
                        </div>
                        <h4 className="text-xs font-medium mb-0.5 text-white/90 truncate">{product.name}</h4>
                        <p className="text-[10px] text-white/60 mb-1 line-clamp-2">
                          {product.description || 'No description available'}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
} 